import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_PROJECT_WIDGETS_URL } from '../components/constants';
import { authHeaders } from '../utils/authHeaders';

export const fetchWidgetData = createAsyncThunk(
  'projectWidgets/fetchData',
  async (historyId) => {
    const res = await axios.get(`${BASE_PROJECT_WIDGETS_URL}/${historyId}`, authHeaders());
    return res.data;
  }
);

export const fetchPrompts = createAsyncThunk(
  'projectWidgets/fetchPrompts',
  async (historyId) => {
    const res = await axios.get(`${BASE_PROJECT_WIDGETS_URL}/${historyId}/prompts`, authHeaders());
    return res.data;
  }
);

export const updatePrompt = createAsyncThunk(
  'projectWidgets/updatePrompt',
  async ({ historyId, widgetKey, promptText }) => {
    const res = await axios.put(
      `${BASE_PROJECT_WIDGETS_URL}/${historyId}/prompts/${widgetKey}`,
      { prompt_text: promptText },
      authHeaders()
    );
    return { widgetKey, promptText, ...res.data };
  }
);

export const generateWidget = createAsyncThunk(
  'projectWidgets/generate',
  async ({ historyId, widgetKey }) => {
    const res = await axios.post(
      `${BASE_PROJECT_WIDGETS_URL}/${historyId}/generate/${widgetKey}`,
      null,
      authHeaders()
    );
    return res.data;
  }
);

const initialState = {
  historyId: null,       // the project (history) UUID this state belongs to
  taskId: null,
  layout: [],            // [{ widget_key, position, is_enabled }] — from the project's agent
  widgets: {},           // { summary: {...}, location: {...}, submission_date: {...} }
  derived: {             // aggregates from backend
    checklist: { accepted: 0, rejected: 0, further_review: 0, info_missing: 0, pending: 0 },
    analysis_score: { percentage: 0, accepted: 0, total: 0 },
    document_count: { count: 0 },
    participants: [],
  },
  isOverviewEnabled: true,   // admin per-agent on/off; default ON until backend says otherwise
  overviewLocked: false,
  showProjectDetail: true,   // admin per-agent: show ProjectDetail screen or go direct to chat
  showFilesTab: true,        // admin per-agent: show Files tab in chat sidebar
  allowLocalFiles: true,     // admin per-agent: allow local file uploads
  generatingKey: null,   // which widget is currently regenerating
  status: 'idle',
  error: null,
};

const slice = createSlice({
  name: 'projectWidgets',
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWidgetData.pending, (s, a) => { s.status = 'loading'; s.isOverviewEnabled = true; s.overviewLocked = false; s.showProjectDetail = true; s.showFilesTab = true; s.allowLocalFiles = true; s.historyId = a.meta.arg; })
      .addCase(fetchWidgetData.fulfilled, (s, a) => {
        s.status = 'succeeded';
        s.historyId = a.payload.history_id || a.meta.arg;
        s.taskId = a.payload.task_id || null;
        s.layout = a.payload.layout || [];
        s.widgets = a.payload.widgets || {};
        s.derived = a.payload.derived || s.derived;
        s.isOverviewEnabled =
          a.payload.is_overview_enabled === undefined
            ? true
            : !!a.payload.is_overview_enabled;
        s.overviewLocked = !!a.payload.overview_locked;
        s.showProjectDetail =
          a.payload.show_project_detail === undefined
            ? true
            : !!a.payload.show_project_detail;
        s.showFilesTab =
          a.payload.show_files_tab === undefined
            ? true
            : !!a.payload.show_files_tab;
        s.allowLocalFiles =
          a.payload.allow_local_files === undefined
            ? true
            : !!a.payload.allow_local_files;
      })
      .addCase(fetchWidgetData.rejected, (s, a) => {
        s.status = 'failed';
        s.error = a.error?.message;
      })
      .addCase(fetchPrompts.fulfilled, (s, a) => {
        s.prompts = a.payload.prompts || [];
      })
      .addCase(updatePrompt.fulfilled, (s, a) => {
        const { widgetKey, promptText } = a.payload;
        const idx = s.prompts.findIndex(p => p.widget_key === widgetKey);
        if (idx >= 0) s.prompts[idx].prompt_text = promptText;
        else s.prompts.push({ widget_key: widgetKey, prompt_text: promptText });
      })
      .addCase(generateWidget.pending, (s, a) => {
        s.generatingKey = a.meta.arg.widgetKey;
        s.error = null;
      })
      .addCase(generateWidget.fulfilled, (s, a) => {
        s.generatingKey = null;
        s.widgets[a.payload.widget_key] = {
          ...a.payload.value,
          generated_at: a.payload.generated_at,
        };
      })
      .addCase(generateWidget.rejected, (s, a) => {
        s.generatingKey = null;
        s.error = a.error?.message;
      });
  },
});

export const { reset: resetProjectWidgets } = slice.actions;
export default slice.reducer;
